/**
 * useRecentCompanies — React hook for localStorage recent company list
 * Stores last 5 selected companies, newest first
 */
import { useState, useCallback } from 'react';

const STORAGE_KEY = 'rcq_recent_companies';
const MAX_RECENT  = 5;

function load() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}

export default function useRecentCompanies() {
  const [recent, setRecent] = useState(() => load());

  /**
   * Add a company to the recent list.
   * Moves it to the top if already present.
   * Stores user_name so the next quick-login can prefill the username (not the client_id).
   */
  const addRecent = useCallback((company) => {
    if (!company?.client_id) return;
    setRecent(prev => {
      const filtered = prev.filter(c => c.client_id !== company.client_id);
      const updated  = [{ ...company, selected_at: new Date().toISOString() }, ...filtered].slice(0, MAX_RECENT);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  /** Remove a specific company from the list */
  const removeRecent = useCallback((clientId) => {
    setRecent(prev => {
      const updated = prev.filter(c => c.client_id !== clientId);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  /** Clear all recent companies */
  const clearRecent = useCallback(() => {
    setRecent([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  /** Get the most recently used company */
  const lastUsed = recent[0] || null;

  return { recent, lastUsed, addRecent, removeRecent, clearRecent };
}
